import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import UserForm from "./UserForm";
import UserFilterBar from "./UserFilterBar";
import storage from "../../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import appActions from "../../../appActions";
import UserHeader from "./UserHeader";
import PrimeTable from "../../../components/PrimeTable";
import Sidebar from "../../../components/Sidebar";
import { useNavigate } from "react-router";

const UserList = () => {

    const dispatch = useDispatch();
    let navigate = useNavigate();

    const userInfo = useMemo(() => JSON.parse(storage.get('loginUser')), []);

    if (!userInfo) { 
        navigate("/login", { replace: true });
    }

    const { userList, userDetails } = useSelector((state) => state?.user);

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder : -1,
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [filterParams, setFilterParams] = useState({});

    // New states for dialog control and selected user
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);

    const fetchUsers = useCallback((params = {}) => {
        dispatch(appActions.USERS_GET_ALL_REQUEST({ ...defaultParams, ...params }));
    }, [dispatch, defaultParams]);

    const handleFilterTask = useCallback((params) => {
        setFilterParams(params);
        fetchUsers(params);
    }, [fetchUsers]);

    useEffect(() => {
        fetchUsers();
    }, [dispatch]);

    const handleViewRedirect = (record) => {
        const data = userList?.data?.find((t) => t.id === record.id);
        setSelectedUser(data);
        setIsViewMode(true);
        setIsEditMode(false);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleEditRedirect = (record) => {
        const data = userList?.data?.find((t) => t.id === record.id);
        setSelectedUser(data);
        setIsEditMode(true);
        setIsViewMode(false);
        setIsDialogVisible(true); // Open dialog in edit mode
    };

    const handleDelete = useCallback((record) => {
        const { id } = record;
        dispatch(appActions.USERS_DELETE_REQUEST({ id }));
    }, [dispatch]);

    const handleDialogClose = () => {
        setIsDialogVisible(false);
        setSelectedUser(null);
    };

    const handleDialogSave = () => {
        fetchUsers();
    };

    const handleSearch = useCallback(
        (params) => {

            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };

            dispatch(appActions.USERS_GET_ALL_REQUEST(searchParams));
        },
        [dispatch],
    );

    const onFilterChangeCallback  = useCallback(
        (event) => {

            const { sortField, sortOrder, rows, page } = event;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); 
        },

        [handleSearch, formValues],
    );
    
    useEffect(() => {
        if (userDetails?.action === 'delete' || userDetails?.action === 'create' || userDetails?.action === 'update') {
            fetchUsers();
        }
    }, [userDetails, fetchUsers]);

    const columns = [
        { field: 'firstName', header: 'First Name', sortable: true},
        { field: 'lastName', header: 'Last Name', sortable: true},
        { field: 'userId', header: 'User ID', sortable: true},
        { field: 'emailId', header: 'Email ID', sortable: true},
        { field: 'authType', header: 'Auth Type', sortable: true},
        { field: 'siteId', header: 'Site ID', sortable: true},
        { field: 'userRole', header: 'User Role', sortable: true},
        { field: 'status', header: 'Status', sortable: true},
    ];

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete],
    );

    return (
        <div className="w-full">
           
            <UserHeader />
            <div className="flex flex-min-height">
                <Sidebar />
                <div className="p-grid w-full pl-60px">
                    <h3 className="title">User Management</h3>
                    <UserFilterBar
                        handleFilter={handleFilterTask}
                        filterParams={filterParams}
                        userInfo={userInfo}
                    />
                    <PrimeTable
                        list={userList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        tableName='user_management'
                        isLazy={true}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        isExport={true}
                    />
                </div>

                {/* Dialog for User Form */}
                <Dialog
                    visible={isDialogVisible}
                    header={isEditMode ? "Edit User" : "View User"}
                    onHide={handleDialogClose}
                    style={{ width: '50vw' }}
                >
                    <UserForm
                        user={selectedUser}
                        isEditMode={isEditMode}
                        isViewMode={isViewMode}
                        onClose={handleDialogClose}
                        onSave={handleDialogSave}
                    />
                </Dialog>
            </div>
        </div>
    );
}

export default UserList;
